// src/components/AdminDashboard.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { auth, db } from "../../../firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";

// --- IMPORTED COMPONENTS ---
import AccountManagement from "../account/AccountManagement";
import DSATManagement from "../dsat/DSATManagement";
import AccessControl from "../dsat/AccessControl";
import ACTManagement from "../act/ACTManagement";
import ACTStudentReportViewer from "../act/report/ACTStudentReportViewer";
import ArchivedACTStudentReportViewer from "../act/report/ArchivedACTStudentReportViewer";
import StudentReportViewer from "../dsat/reports/StudentReportViewer";
import AdminScheduling from "../scheduling/AdminScheduling";
import ActivityLogView from "../scheduling/ActivityLogView"; 
import StudentManagement from "../student/StudentManagement";
import EmployeeManagement from "../employee/EmployeeManagement"; 
import AdminAnnouncements from "../announcements/AdminAnnouncements";
import StudentCalendar from "../scheduling/student_calendar/StudentCalendar";
import ArchivedReportViewer from "../dsat/reports/ArchivedReportViewer";
import SubjectMatrix from "../subject/SubjectMatrix";
import CurriculumApp from "../curriculum/CurriculumApp";
import AdminAccessControl from "../access_control/AdminAccessControl";
import SuggestionBox from "../../suggestions/SuggestionBox";
import useSuggestionBadge from "../../suggestions/useSuggestionBadge";
import SessionLogsWorkspace from "../../session_logs/SessionLogsWorkspace";
import AdminSettings from "../settings/AdminSettings";
import PSATManagement from "../psat/PSATManagement";
import PSATStudentReportViewer from "../psat/reports/PSATStudentReportViewer";
import ScheduleActivityToast from "../scheduling/components/ScheduleActivityToast";
import DiagnosticReports from "../diagnostics/DiagnosticReports";
import StudentPlansPage from "../plans/StudentPlansPage";
import DiagnosticContentTools from "../diagnostics/DiagnosticContentTools";
import { buildDiagnosticReportUrl } from "../diagnostics/diagnosticReportsData";
import AdminHomeScreen from "./home/AdminHomeScreen";
import { loadAttentionCounts } from "./home/homeScreenData";

import {
  FiExternalLink,
  FiLogOut,
  FiSettings,
  FiChevronLeft,
  FiChevronRight,
  FiLayers,
  FiFileText,
  FiHome,
  FiMessageSquare,
} from "react-icons/fi";

// New Icons
import { TfiAnnouncement } from "react-icons/tfi";
import { PiStudent, PiBooks } from "react-icons/pi";
import { FaRegCalendar } from "react-icons/fa6";
import { BsPerson } from "react-icons/bs";
import { MdManageAccounts, MdSecurity } from "react-icons/md";

// --- STYLES ---
import "./AdminDashboardSidebar.css";
import {
  ADMIN_DEFAULT_TAB,
  getAdminPathForTab,
  getAdminTabFromPath,
  getCanonicalAdminPath,
  getAdminSectionRootRedirect,
  getFallbackAdminPath,
  getGroupKeyForAdminTab,
  shouldPreserveAdminSearch,
} from "./adminPortalRoutes";

export default function AdminDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);
  
  // Open states for groups
  const [openGroups, setOpenGroups] = useState({
    employee: false,
    dsat: false,
    scheduling: true, // Default open page
    student: false,
    session: false,
  });

  // User Profile State
  const [userProfile, setUserProfile] = useState({ uid: "", name: "", email: "" });
  const [blockedPages, setBlockedPages] = useState([]); // Array of keys to hide
  const [hasResolvedBlockedPages, setHasResolvedBlockedPages] = useState(false);
  const activeTab = useMemo(
    () => getAdminTabFromPath(location.pathname) || ADMIN_DEFAULT_TAB,
    [location.pathname]
  );

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          const userDocRef = doc(db, "users", currentUser.uid);
          const userSnap = await getDoc(userDocRef);
          
          if (userSnap.exists()) {
            const data = userSnap.data();
            setUserProfile({
              uid: currentUser.uid,
              name: data.name || currentUser.displayName || "User",
              email: data.email || currentUser.email
            });
            // --- Load blocked pages ---
            if (Array.isArray(data.blockedPages)) {
              setBlockedPages(data.blockedPages);
            } else {
              setBlockedPages([]);
            }
          } else {
            setUserProfile({
              uid: currentUser.uid,
              name: currentUser.displayName || "Admin",
              email: currentUser.email
            });
          }
        } catch (err) {
          console.error("Error fetching user profile:", err);
          setBlockedPages([]);
        }
      }
      setHasResolvedBlockedPages(true);
    });
    return () => unsubscribe();
  }, []);

  const userInitial = useMemo(() => (userProfile.name?.trim()?.[0] || "U").toUpperCase(), [userProfile.name]);

  // --- Home sidebar attention badge ---
  // Total of unreviewed new sign-ups + Missing & Needs Attention items, mirroring
  // the Home dashboard. Polls periodically, refreshes on window focus, and reacts
  // instantly to the "admin-attention-refresh" event (dispatched when an admin
  // reviews a student or corrects an auto clock-out).
  const [homeBadgeCount, setHomeBadgeCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        const counts = await loadAttentionCounts({ now: new Date() });
        if (!cancelled) setHomeBadgeCount(counts.total || 0);
      } catch {
        /* leave the previous count in place on a transient failure */
      }
    };
    refresh();
    const interval = setInterval(refresh, 60000);
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    window.addEventListener("admin-attention-refresh", refresh);
    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("admin-attention-refresh", refresh);
    };
  }, []);

  const handleLogout = () => {
    auth.signOut().catch((err) => console.error("Logout Error:", err));
  };

  // --- Suggestions sidebar badge ---
  // Red count = suggestions completed since the tab was last opened; grey count
  // = read but not completed (completed overrides read). The blue `newCount` is
  // unread suggestions submitted since then — it renders ALONGSIDE the red/grey
  // badge, never instead of it. Opening the Suggestions tab marks everything
  // seen, so all of these clear on click.
  const {
    count: suggestionsBadgeCount,
    tone: suggestionsBadgeTone,
    newCount: suggestionsNewCount,
    markSeen: markSuggestionsSeen,
  } = useSuggestionBadge({ uid: userProfile.uid, isAdmin: true });

  useEffect(() => {
    if (activeTab === "suggestions") markSuggestionsSeen();
  }, [activeTab, markSuggestionsSeen]);

  // The collapsed sidebar has room for one dot only, so the status badge keeps
  // priority and blue is used when there is nothing else to flag.
  const suggestionsDotClass =
    suggestionsBadgeCount > 0
      ? suggestionsBadgeTone === "muted"
        ? "ad-navDot ad-navDot--muted"
        : "ad-navDot"
      : "ad-navDot ad-navDot--info";

  const navigateToAdminTab = useCallback(
    (tabKey) => {
      const nextPath = getAdminPathForTab(tabKey);
      if (!nextPath) return;
      navigate(nextPath);
    },
    [navigate]
  );

  // Bumping this remounts the active content — used to "refresh" the current
  // page when its already-active sidebar item is clicked again.
  const [contentKey, setContentKey] = useState(0);

  // Click handler for leaf nav items / sub-items. Plain left-click navigates
  // via the SPA. Clicking the section you are ALREADY in returns to that
  // section's ROOT — a student profile (/students/directory/<id>) goes back to
  // the full directory list, a curriculum page back to the hub — and only when
  // the URL is already the root do we re-mount the page to refresh it.
  // Modifier / middle clicks fall through so the browser can open a new tab.
  const handleNavClick = useCallback(
    (tabKey) => (e) => {
      if (e && (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1)) return;
      if (e) e.preventDefault();

      if (tabKey === activeTab) {
        const rootRedirect = getAdminSectionRootRedirect(tabKey, location.pathname);
        if (rootRedirect) {
          navigate(rootRedirect, { replace: false });
          return;
        }
        setContentKey((k) => k + 1);
        return;
      }
      navigateToAdminTab(tabKey);
    },
    [activeTab, location.pathname, navigate, navigateToAdminTab]
  );

  const toggleGroup = (groupKey) => {
    setOpenGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  useEffect(() => {
    const activeGroupKey = getGroupKeyForAdminTab(activeTab);
    if (!activeGroupKey) return;
    setOpenGroups((prev) => (prev[activeGroupKey] ? prev : { ...prev, [activeGroupKey]: true }));
  }, [activeTab]);

  useEffect(() => {
    if (!hasResolvedBlockedPages) return;

    const fallbackPath = getFallbackAdminPath(blockedPages);
    const routeTabKey = getAdminTabFromPath(location.pathname);

    if (routeTabKey && blockedPages.includes(routeTabKey)) {
      if (location.pathname !== fallbackPath) {
        navigate(fallbackPath, { replace: true });
      }
      return;
    }

    const canonicalPath = getCanonicalAdminPath(location.pathname, activeTab) || fallbackPath;
    const shouldPreserveSearch =
      canonicalPath === location.pathname && shouldPreserveAdminSearch(location.pathname);
    const nextPath = shouldPreserveSearch ? `${canonicalPath}${location.search}` : canonicalPath;

    if (`${location.pathname}${location.search}` !== nextPath) {
      navigate(nextPath, { replace: true });
    }
  }, [activeTab, blockedPages, hasResolvedBlockedPages, location.pathname, location.search, navigate]);

// --- NAVIGATION STRUCTURE ---
  const rawNavItems = [
    {
      key: "home",
      label: "Home",
      icon: <FiHome size={18} />,
      onClick: () => navigateToAdminTab("home"),
    },

    {
      key: "announcements",
      label: "Announcements",
      icon: <TfiAnnouncement size={18} />, 
      onClick: () => navigateToAdminTab("announcements") 
    },

    {
      key: "suggestions",
      label: "Suggestions",
      icon: <FiMessageSquare size={18} />,
      onClick: () => navigateToAdminTab("suggestions")
    },
    
    // --- SCHEDULING ---
    { 
      key: "scheduling", 
      label: "Scheduling", 
      icon: <FaRegCalendar size={18} />, 
      isGroup: true,
      children: [
        { key: "scheduling__calendar", label: "Schedule", onClick: () => navigateToAdminTab("scheduling__calendar") },
        { key: "scheduling__logs", label: "Activity Logs", onClick: () => navigateToAdminTab("scheduling__logs") },
        { key: "scheduling__subjects", label: "Subjects", onClick: () => navigateToAdminTab("scheduling__subjects") },
      ]
    },

    // --- STUDENTS ---
    { 
      key: "student", 
      label: "Students", 
      icon: <PiStudent size={18} />, 
      isGroup: true, 
      children: [
        { key: "student__directory", label: "Directory", onClick: () => navigateToAdminTab("student__directory") },
        { key: "student__plans", label: "Plans", onClick: () => navigateToAdminTab("student__plans") },
        { key: "scheduling__student", label: "Calendar", onClick: () => navigateToAdminTab("scheduling__student") },
      ]
    },
    
    // --- DIAGNOSTICS ---
    { 
      key: "dsat", 
      label: "Diagnostics", 
      icon: <FiLayers size={18} />, 
      isGroup: true,
      children: [
        { key: "dsat__access", label: "Test Access", onClick: () => navigateToAdminTab("dsat__access") },
        { key: "diag__reports", label: "Diagnostic Reports", onClick: () => navigateToAdminTab("diag__reports") },
        { key: "diag__archived", label: "Archived Diagnostic Reports", onClick: () => navigateToAdminTab("diag__archived") },
        { key: "diag__content", label: "Content & Tools", onClick: () => navigateToAdminTab("diag__content") },
      ]
    },

    // --- CURRICULUM (single hub; libraries are managed dynamically) ---
    {
      key: "curriculum",
      label: "Curriculum",
      icon: <PiBooks size={18} />,
      onClick: () => navigateToAdminTab("curriculum"),
    },

    // --- EMPLOYEE ---
    {
      key: "employee",
      label: "Employee",
      icon: <BsPerson size={18} />, 
      isGroup: true,
      children: [
        { key: "employee__directory", label: "Directory", onClick: () => navigateToAdminTab("employee__directory") },
        { key: "employee__availability", label: "Calendar", onClick: () => navigateToAdminTab("employee__availability") },
        
        // These can now be blocked!
        { key: "employee__rate", label: "Pay Rate", onClick: () => navigateToAdminTab("employee__rate") },
        { key: "employee__payroll", label: "Payroll", onClick: () => navigateToAdminTab("employee__payroll") },
        { key: "employee__logging", label: "Log Hours", onClick: () => navigateToAdminTab("employee__logging") },
      ],
    },

    {
      key: "session",
      label: "Session",
      icon: <FiFileText size={18} />,
      isGroup: true,
      children: [
        { key: "session__log", label: "Log", onClick: () => navigateToAdminTab("session__log") },
        { key: "session__progress", label: "Progress Report", onClick: () => navigateToAdminTab("session__progress") },
      ],
    },

    { 
      key: "account", 
      label: "Account", 
      icon: <MdManageAccounts size={18} />,
      onClick: () => navigateToAdminTab("account") 
    },

    // --- ACCESS CONTROL (Master Admin) ---
    { 
      key: "access_control", 
      label: "Access Control", 
      icon: <MdSecurity size={18} />,
      onClick: () => navigateToAdminTab("access_control") 
    },
    
  ];

  // Filter function to hide blocked items
  const navItems = useMemo(() => {
    return rawNavItems.map(item => {
      // 1. If the main item itself is blocked, return null (filter it out later)
      if (blockedPages.includes(item.key)) return null;

      // 2. If it has children, filter the children
      if (item.children) {
        const filteredChildren = item.children.filter(child => !blockedPages.includes(child.key));
        // Return item with filtered children
        return { ...item, children: filteredChildren };
      }

      return item;
    }).filter(Boolean); // Remove nulls
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockedPages]);

  // --- RENDER CONTENT ---
  const renderMain = () => {
    // 0. Home (landing dashboard)
    if (activeTab === "home") {
      return <div style={{ overflow: "hidden", height: "100%" }}><AdminHomeScreen /></div>;
    }

    // 1. Employee Section
    if (activeTab.startsWith("employee__")) {
      const subMap = {
        "employee__directory": "directory",
        "employee__availability": "availability",
        "employee__rate": "rate",
        "employee__payroll": "payroll",
        "employee__logging": "logging",
      };
      return <EmployeeManagement activeSubPage={subMap[activeTab]} />;
    }

    // 2. dSAT Section
    // Unified Diagnostic Reports hub — search a student, see all SAT/PSAT/ACT
    // (and archived) attempts, and open the existing full report in a new tab.
    if (activeTab === "diag__reports") {
      return (
        <div style={{ overflowY: "auto", height: "100%" }}>
          <DiagnosticReports mode="active" canDelete getReportUrl={(attempt) => buildDiagnosticReportUrl(attempt)} />
        </div>
      );
    }
    if (activeTab === "diag__archived") {
      return (
        <div style={{ overflowY: "auto", height: "100%" }}>
          <DiagnosticReports mode="archived" canDelete getReportUrl={(attempt) => buildDiagnosticReportUrl(attempt)} />
        </div>
      );
    }
    if (activeTab === "diag__content") return <div style={{ overflow: "hidden", height: "100%" }}><DiagnosticContentTools /></div>;
    if (activeTab === "dsat__report") return <div style={{ overflowY: "auto", height: "100%", padding: "20px" }}><StudentReportViewer /></div>;
    if (activeTab === "act__report") return <div style={{ overflowY: "auto", height: "100%", padding: "20px" }}><ACTStudentReportViewer /></div>;
    if (activeTab === "curriculum") return <div style={{ overflow: "hidden", height: "100%" }}><CurriculumApp basePath="/curriculum" permissions={{ canDelete: true }} /></div>;
    if (activeTab === "act__archived") return <div style={{ overflowY: "auto", height: "100%", padding: "20px" }}><ArchivedACTStudentReportViewer /></div>;
    if (activeTab === "dsat__archived") return <div style={{ overflowY: "auto", height: "100%", padding: "20px" }}><ArchivedReportViewer /></div>;
    if (activeTab === "dsat__content") return <DSATManagement />;
    if (activeTab === "act__content") return <ACTManagement />;
    if (activeTab === "psat__report") return <div style={{ overflowY: "auto", height: "100%", padding: "20px" }}><PSATStudentReportViewer /></div>;
    if (activeTab === "psat__content") return <PSATManagement />;
    if (activeTab === "dsat__access") return <AccessControl />;
    if (activeTab === "dsat") return <div style={{ padding: 20 }}>Select a dSAT option from the sidebar.</div>;

    // 3. Scheduling Section
    if (activeTab === "scheduling__calendar") return <div style={{ overflow: "hidden", height: "100%" }}><AdminScheduling /></div>;
    if (activeTab === "scheduling__logs") return <div style={{ overflow: "hidden", height: "100%" }}><ActivityLogView /></div>;
    if (activeTab === "scheduling__subjects") return <div style={{ overflow: "hidden", height: "100%" }}><SubjectMatrix /></div>;
    if (activeTab === "scheduling") return <div style={{ padding: 20 }}>Select Calendar or Logs.</div>;

    // 4. Student Section (New Logic)
    if (activeTab === "student__directory" || activeTab === "student") return <div style={{ overflow: "hidden", height: "100%" }}><StudentManagement /></div>;
    if (activeTab === "student__plans") return <div style={{ overflow: "auto", height: "100%" }}><StudentPlansPage viewerRole="admin" /></div>;
    if (activeTab === "scheduling__student") return <div style={{ overflow: "hidden", height: "100%" }}><StudentCalendar /></div>;

    // 5. Other Sections
    switch(activeTab) {
      case "announcements": return <div style={{ overflow: "hidden", height: "100%" }}><AdminAnnouncements /></div>;
      case "suggestions": return <div style={{ overflow: "hidden", height: "100%" }}><SuggestionBox viewerRole="admin" /></div>;
      case "session__log": return <div style={{ overflowY: "auto", height: "100%" }}><SessionLogsWorkspace viewerRole="admin" page="log" /></div>;
      case "session__progress": return <div style={{ overflowY: "auto", height: "100%" }}><SessionLogsWorkspace viewerRole="admin" page="progress" /></div>;
      case "session": return <div style={{ padding: 20 }}>Select Log or Progress Report.</div>;
      case "access_control": return <div style={{ overflowY: "auto", height: "100%" }}><AdminAccessControl /></div>;
      case "account": return <div style={{ overflowY: "auto", height: "100%" }}><AccountManagement /></div>;
      case "settings": return <div style={{ overflow: "hidden", height: "100%" }}><AdminSettings /></div>;
      default: return <div style={{ padding: 20 }}>Select a section.</div>;
    }
  };

  return (
    <div className="ad-shell">
      {/* SIDEBAR */}
      <aside className={`ad-sidebar ${isSidebarExpanded ? "ad-sidebar--expanded" : "ad-sidebar--collapsed"}`}>
        <div className="ad-sidebarHeader">
          {isSidebarExpanded ? (
            <>
              <a
                className="ad-brandRow ad-brandRow--link"
                href={getAdminPathForTab("home") || "/home"}
                onClick={handleNavClick("home")}
                title="Go to Home"
              >
                <div className="ad-brandLogo" aria-hidden="true">
                  <img src="/images/logo.png" alt="Logo" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                </div>
                {/* Updated Brand Column */}
                <div className="td-brandCol">
                  <div className="td-brandName">True Education</div>
                  <div className="td-brandSubtitle">Admin Portal</div>
                </div>
              </a>
              <button
                className="ad-collapseBtn"
                onClick={() => setIsSidebarExpanded(false)}
                aria-label="Collapse sidebar"
              >
                <FiChevronLeft size={18} />
              </button>
            </>
          ) : (
            <button
              className="ad-collapseBtn"
              onClick={() => setIsSidebarExpanded(true)}
              aria-label="Expand sidebar"
              style={{ margin: "0 auto" }}
            >
              <FiChevronRight size={18} />
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="ad-nav">
          {navItems.map((item) => {
            const isGroup = !!item.isGroup;
            
            // Check if active tab matches parent key OR any child key (including nested sub-children)
            const activeChild = item.children?.some((c) => 
              c.key === activeTab || c.children?.some(sub => sub.key === activeTab)
            );
            
            // Logic for Student Directory specifically
            const isActive = item.key === activeTab || (item.key === 'student' && activeTab === 'student__directory');
            
            if (isGroup) {
              const isOpen = !!openGroups[item.key];
              
              return (
                <div className="ad-navGroup" key={item.key}>
                  {/* Level 1: Main Group Header (e.g., Diagnostics) */}
                  <div
                    className={`ad-navItem ${activeChild ? "ad-navItem--active" : ""}`}
                    data-tooltip={item.label}
                    onClick={() => toggleGroup(item.key)}
                  >
                    <div className="ad-navIcon">{item.icon}</div>
                    {isSidebarExpanded && <div className="ad-navLabel">{item.label}</div>}
                    <div className="ad-navRight">
                      {isSidebarExpanded && (
                        <span className="ad-navChevron" style={{transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s', display: 'flex'}}>
                          <FiChevronRight size={14} />
                        </span>
                      )}
                    </div>
                    {!isSidebarExpanded && <span className="ad-tooltip">{item.label}</span>}
                  </div>

                  {/* Level 2: Children */}
                  {isOpen && isSidebarExpanded && (
                    <div className="ad-subNav">
                      {item.children.map((child) => {
                        // Check if this child is actually a Sub-Group (Folder) like SAT/ACT
                        if (child.isSubGroup) {
                          const isSubOpen = !!openGroups[child.key];
                          const activeSubChild = child.children?.some(sub => sub.key === activeTab);

                          return (
                            <div key={child.key}>
                              {/* Level 2: Sub-Group Header (SAT/ACT) */}
                              <div 
                                className={`ad-subNavItem ${activeSubChild ? "ad-subNavItem--active" : ""}`}
                                onClick={() => toggleGroup(child.key)}
                                style={{ justifyContent: 'space-between' }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                  <span className="ad-subNavDot" style={{ background: activeSubChild ? 'var(--ad-blue)' : 'transparent', border: activeSubChild ? 'none' : '1px solid #cbd5e1' }} />
                                  <span className="ad-subNavLabel">{child.label}</span>
                                </div>
                                <FiChevronRight size={12} style={{ transform: isSubOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}/>
                              </div>

                              {/* Level 3: Nested Items (SAT Reports, etc.) */}
                              {isSubOpen && (
                                <div className="ad-subNav" style={{ marginLeft: 6, borderLeft: '1px solid #e2e8f0' }}>
                                  {child.children.map(subChild => (
                                    <a
                                      key={subChild.key}
                                      href={getAdminPathForTab(subChild.key) || "#"}
                                      className={`ad-subNavItem ${subChild.key === activeTab ? "ad-subNavItem--active" : ""}`}
                                      onClick={handleNavClick(subChild.key)}
                                    >
                                      <span className="ad-subNavDot" />
                                      <span className="ad-subNavLabel">{subChild.label}</span>
                                    </a>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        }

                        // Standard Level 2 Item (e.g., Test Access)
                        const childActive = child.key === activeTab;
                        return (
                          <a
                            key={child.key}
                            href={getAdminPathForTab(child.key) || "#"}
                            className={`ad-subNavItem ${childActive ? "ad-subNavItem--active" : ""}`}
                            onClick={handleNavClick(child.key)}
                          >
                            <span className="ad-subNavDot" />
                            <span className="ad-subNavLabel">{child.label}</span>
                          </a>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            // Standard Non-Group Items
            return (
              <a
                key={item.key}
                href={getAdminPathForTab(item.key) || "#"}
                className={`ad-navItem ${isActive ? "ad-navItem--active" : ""}`}
                data-tooltip={item.label}
                onClick={handleNavClick(item.key)}
              >
                <div className="ad-navIcon">{item.icon}</div>
                {isSidebarExpanded && <div className="ad-navLabel">{item.label}</div>}
                <div className="ad-navRight">
                  {item.isExternal && isSidebarExpanded && <span className="ad-extMark"><FiExternalLink size={12}/></span>}
                  {item.key === "home" && homeBadgeCount > 0 && isSidebarExpanded && (
                    <span className="ad-badge">{homeBadgeCount > 99 ? "99+" : homeBadgeCount}</span>
                  )}
                  {item.key === "suggestions" && isSidebarExpanded && (suggestionsNewCount > 0 || suggestionsBadgeCount > 0) && (
                    <span className="ad-badgeGroup">
                      {/* Blue: unread suggestions submitted since the last visit. */}
                      {suggestionsNewCount > 0 && (
                        <span className="ad-badge ad-badge--info">
                          {suggestionsNewCount > 99 ? "99+" : suggestionsNewCount}
                        </span>
                      )}
                      {/* Red / grey: completed / read status changes. */}
                      {suggestionsBadgeCount > 0 && (
                        <span className={`ad-badge ${suggestionsBadgeTone === "muted" ? "ad-badge--muted" : ""}`}>
                          {suggestionsBadgeCount > 99 ? "99+" : suggestionsBadgeCount}
                        </span>
                      )}
                    </span>
                  )}
                </div>
                {/* Collapsed sidebar: show a small dot so admins still notice items needing attention. */}
                {item.key === "home" && homeBadgeCount > 0 && !isSidebarExpanded && (
                  <span className="ad-navDot" aria-hidden="true" />
                )}
                {item.key === "suggestions" &&
                  (suggestionsNewCount > 0 || suggestionsBadgeCount > 0) &&
                  !isSidebarExpanded && (
                    <span className={suggestionsDotClass} aria-hidden="true" />
                  )}
                {!isSidebarExpanded && <span className="ad-tooltip">{item.label}</span>}
              </a>
            );
          })}
        </nav>

        <div className="ad-spacer" />

        <div className="ad-footerNav">
          <a
            href={getAdminPathForTab("settings") || "/settings"}
            className={`ad-navItem ${activeTab === "settings" ? "ad-navItem--active" : ""}`}
            onClick={handleNavClick("settings")}
            data-tooltip="Settings"
          >
            <div className="ad-navIcon"><FiSettings size={18} /></div>
            {isSidebarExpanded && <div className="ad-navLabel">Settings</div>}
            {!isSidebarExpanded && <span className="ad-tooltip">Settings</span>}
          </a>
          <div className="ad-navItem" onClick={handleLogout} data-tooltip="Logout">
            <div className="ad-navIcon"><FiLogOut size={18} /></div>
            {isSidebarExpanded && <div className="ad-navLabel">Logout</div>}
            {!isSidebarExpanded && <span className="ad-tooltip">Logout</span>}
          </div>
        </div>

        {/* User Profile */}
        <div className="ad-userCard">
          <div className="ad-avatar">{userInitial}</div>
          {isSidebarExpanded && (
            <div className="ad-userInfo">
              <div className="ad-userName">{userProfile.name}</div>
              <div className="ad-userEmail">{userProfile.email}</div>
            </div>
          )}
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <div className="ad-main">
        <div className="ad-mainInner">
          <div className="ad-panel">
            <div className="ad-panelInner" key={contentKey} style={{ height: "100%" }}>
              {renderMain()}
            </div>
          </div>
        </div>
      </div>

      {/* Global activity toast — persists across all admin pages */}
      <ScheduleActivityToast />
    </div>
  );
}
